import {
  ColorClass,
  Divider,
  Icon,
  IconIdentifier,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import { selectMap3dDataset } from 'map-3d/shared/map-3d-slices';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileType, useBulkAddFile, useDeleteFile } from 'shared/api';
import { processStoreFiles } from 'shared/helpers';
import { AttachmentFileCard } from './attachment-file-card';
import { SupportedFormatsTooltip } from './supported-formats-tooltip';
import {
  type Attachment,
  type DashboardAttachmentsProps,
  DashboardModalStep,
} from './types';

// Constants.
const SUPPORTED_FILE_FORMATS =
  '.csv, .pdf, .txt, .jpg, .jpeg, .png, .mp4, .mkv, .zip, .mp3, .wav';

export const DashboardAttachments: React.FC<DashboardAttachmentsProps> = ({
  handleBack,
  attachments,
  setAttachments,
  currentStep,
  shouldCancelUploads,
  setIsUploadingFiles,
  isDropZoneDisabled,
}) => {
  // States.
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>(
    {},
  );

  // Refs.
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Apis.
  const { mutateAsync: createBulkAttachmentFiles } = useBulkAddFile();
  const { mutate: sendDeleteFileRequest } = useDeleteFile();

  // Selectors.
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);

  // Memos.
  const activeUploadsCount = useMemo(() => {
    return Object.values(uploadingFiles).filter(Boolean).length;
  }, [uploadingFiles]);

  // Effects.
  useEffect(() => {
    setIsUploadingFiles(activeUploadsCount > 0);
  }, [activeUploadsCount]);

  // Handlers.
  const handleFileSelection = async (selectedFiles: FileList | null) => {
    if (isEmpty(selectedFiles)) {
      return;
    }

    const validFiles = await processStoreFiles(
      selectedFiles,
      attachments,
      false,
    );

    if (isEmpty(validFiles) || isNil(selectedTerrainIteration?.id)) {
      return;
    }

    try {
      const createFilesResponse = await createBulkAttachmentFiles(
        validFiles.map(({ file }) => ({
          filename: file.name,
          filetype: FileType.Attachment,
        })),
      );

      const { data: filesResponseData } = createFilesResponse;

      const filesWithUploadIds = validFiles.map((selectedFile, index) => ({
        ...selectedFile,
        fileUploadId: filesResponseData[index]?.id,
      }));

      setAttachments((prev) => [...filesWithUploadIds, ...prev]);
    } catch (error) {
      console.error('Failed to create dashboard files:', error);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelection(event.dataTransfer.files);
  };

  const onRemoveFile = (localFileId: string, fileUploadId?: string | null) => {
    if (isNil(fileUploadId) || isNil(selectedTerrainIteration?.id)) {
      return;
    }

    sendDeleteFileRequest({
      id: fileUploadId,
    });

    setAttachments((prev) =>
      prev.filter((file: Attachment) => file.id !== localFileId),
    );
  };

  const dropzoneClassName = classNames(
    'dashboard-modal__attachments__dropzone',
    {
      'dashboard-modal__attachments__dropzone--dragging': isDragging,
    },
    {
      'dashboard-modal__attachments__dropzone--disabled': isDropZoneDisabled,
    },
  );

  const handleUploadStatusChange = (fileId: string, isUploading: boolean) => {
    setUploadingFiles((prev) => {
      if (prev[fileId] === isUploading) {
        return prev;
      }

      return {
        ...prev,
        [fileId]: isUploading,
      };
    });
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  return (
    <div
      className="dashboard-modal__attachments"
      hidden={currentStep !== DashboardModalStep.Attachments}
    >
      <div className="dashboard-modal__attachments__container">
        <div className="dashboard-modal__attachments__header">
          <Icon
            identifier={IconIdentifier.ArrowLeft}
            size={24}
            onClick={handleBack}
          />

          <span className="dashboard-modal__attachments__title">
            Attachments (weighbridge slips, field photos)
          </span>
        </div>

        <div
          className={dropzoneClassName}
          onDragLeave={handleDragLeave}
          onDrop={handleFileDrop}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
        >
          <Icon identifier={IconIdentifier.Upload} size={30} />

          <div className="dashboard-modal__attachments__dropzone-text">
            Drag & Drop OR{' '}
            <span className="dashboard-modal__attachments__dropzone-action">
              Choose files
            </span>{' '}
            to upload
          </div>

          <div className="dashboard-modal__attachments__supported-formats">
            <span className="dashboard-modal__attachments__supported-formats-text">
              Upload formats like CSV, PDF, JPEG, PNG, TXT and more
            </span>

            <Tooltip
              hoverText={<SupportedFormatsTooltip />}
              placement={Placement.Top}
            >
              <Icon
                identifier={IconIdentifier.InfoCircle}
                size={18}
                colorClass={ColorClass.Primary500}
              />
            </Tooltip>
          </div>

          <input
            type="file"
            accept={SUPPORTED_FILE_FORMATS}
            multiple
            hidden
            ref={fileInputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleFileSelection(event.target.files)
            }
          />
        </div>
      </div>

      {!isEmpty(attachments) && (
        <div className="dashboard-modal__attachments__files-section">
          <Divider text={`Selected Files (${attachments.length})`} />

          <div className="dashboard-modal__attachments__files-list">
            {attachments.map((file: Attachment) => (
              <AttachmentFileCard
                key={`attachment-file-card-${file.id}`}
                selectedFile={file}
                onRemoveFile={onRemoveFile}
                onUploadStatusChange={handleUploadStatusChange}
                shouldCancelUpload={shouldCancelUploads}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
